import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { inr } from "@/lib/format";

const EMPTY = { vendor_id: "", expected_date: "", notes: "", lines: [{ material_id: "", qty: "", rate: "" }] };

export function PoDialog({ open, onClose, vendors, materials, onDone }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) setF(EMPTY); }, [open]);
  const setLine = (i, k, v) => setF({ ...f, lines: f.lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)) });
  const total = f.lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.rate) || 0), 0);
  const save = async () => {
    try {
      await api.post("/purchase-orders", { ...f, expected_date: f.expected_date || null, lines: f.lines.map((l) => ({ material_id: l.material_id, qty: Number(l.qty) || 0, rate: Number(l.rate) || 0 })) });
      toast.success("Purchase order created"); onClose(); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const sel = "h-9 w-full rounded-md border border-input bg-white px-2 text-sm";
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="po-dialog">
        <DialogHeader><DialogTitle className="font-display">New purchase order</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <select className={sel} value={f.vendor_id} onChange={(e) => setF({ ...f, vendor_id: e.target.value })} data-testid="po-vendor-select">
            <option value="">Pick supplier</option>{vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          {f.lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_70px_80px_auto] gap-2">
              <select className={sel} value={l.material_id} onChange={(e) => setLine(i, "material_id", e.target.value)} data-testid={`po-line-material-${i}`}>
                <option value="">Material</option>{materials.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>)}
              </select>
              <Input type="number" placeholder="Qty" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} data-testid={`po-line-qty-${i}`} />
              <Input type="number" placeholder="Rate" value={l.rate} onChange={(e) => setLine(i, "rate", e.target.value)} data-testid={`po-line-rate-${i}`} />
              <Button size="icon" variant="ghost" onClick={() => setF({ ...f, lines: f.lines.filter((_, j) => j !== i) })} disabled={f.lines.length === 1}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => setF({ ...f, lines: [...f.lines, { material_id: "", qty: "", rate: "" }] })} data-testid="po-add-line-btn"><Plus className="mr-1 h-4 w-4" />Add material</Button>
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" value={f.expected_date} onChange={(e) => setF({ ...f, expected_date: e.target.value })} data-testid="po-expected-input" />
            <Input placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </div>
          <div className="text-right text-sm font-semibold" data-testid="po-total">Total {inr(total)}</div>
        </div>
        <DialogFooter><Button className="bg-[#0F382C] hover:bg-[#134E3A]" onClick={save} data-testid="po-save-btn">Create PO</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function VendorForm({ onDone }) {
  const [f, setF] = useState({ name: "", phone: "", gstin: "", materials: "", payment_terms: "Cash" });
  const save = async () => {
    try { await api.post("/vendors", f); toast.success("Supplier added"); setF({ name: "", phone: "", gstin: "", materials: "", payment_terms: "Cash" }); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-2 sm:grid-cols-6" data-testid="vendor-form">
      {[["name", "Supplier name"], ["phone", "Phone"], ["gstin", "GSTIN"], ["materials", "Supplies (e.g. rice, urad)"], ["payment_terms", "Payment terms"]].map(([k, p]) => (
        <Input key={k} placeholder={p} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`vendor-${k}-input`} />
      ))}
      <Button onClick={save} className="bg-[#0F382C] hover:bg-[#134E3A]" data-testid="vendor-save-btn"><Plus className="mr-1 h-4 w-4" />Add</Button>
    </div>
  );
}

export function usePurchasing() {
  const { user } = useAuth();
  const [data, setData] = useState({ vendors: [], pos: [], materials: [] });
  const load = useCallback(async () => {
    const [v, p, m] = await Promise.all([api.get("/vendors"), api.get("/purchase-orders"), api.get("/materials")]);
    setData({ vendors: v.data, pos: p.data, materials: m.data });
  }, []);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);
  return { ...data, load, isAdmin: user?.role === "admin" };
}
